import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../shared/Seo';

const RETURN_REFUND_SECTIONS = [
  {
    title: '1. All Sales Are Final',
    body:
      'Due to the randomized and collectible nature of capsules, all sales on PonPon are final.\n\nPonPon does not offer returns, exchanges, or refunds for:',
    bullets: [
      'Receiving an unwanted, common, or duplicate character',
      'Dissatisfaction with a randomized spin result',
      '"Change of mind" after purchasing Tokens or spinning',
      'Buyer\'s remorse regarding capsule contents',
    ],
    footnote: 'Random outcomes are a core feature of PonPon and cannot be reversed once revealed.',
  },
  {
    title: '2. Tokens and Digital Goods (Non-Refundable)',
    body:
      'All purchases of Tokens, our digital consumable currency, are final and non-refundable, including:',
    bullets: [
      'Used Tokens',
      'Unused Tokens',
      'Accidental purchases',
      'Token bundles bought during promotions',
    ],
    footnote:
      'Once Tokens are consumed during a spin, the action cannot be undone or refunded.\n\nIf you believe you were charged incorrectly or charged more than once, please contact us and we will investigate.',
  },
  {
    title: '3. Spin Results Cannot Be Returned or Exchanged',
    body:
      'All spin outcomes are determined randomly and cannot be returned, refunded, or exchanged, except in cases of verifiable system error (see Section 8).\n\nThis includes:',
    bullets: [
      'Receiving common or low-rarity figures',
      'Receiving duplicates',
      'Differences in paint, color, or styling typical of capsule toys',
      'Variations within a series (e.g., different poses or accessories)',
    ],
  },
  {
    title: '4. Shipping Fees',
    bullets: [
      'Shipping fees are displayed during the checkout process and are non-refundable once a shipment order is created.',
      'Free shipping unlocks automatically when your Locker reaches the required number of items (e.g., 8 items).',
      'Partial shipments (fewer than 8 items) incur a standard shipping fee.',
      'Shipping charges cannot be refunded if the package is already in transit.',
    ],
  },
  {
    title: '5. Damaged or Defective Items',
    body:
      'Refunds or replacements are only available for verifiable manufacturing defects.\n\nIf you receive a defective item, contact us within 7 days of delivery at salesponpon@gmail.com\n\nPlease include:',
    bullets: [
      'Your shipping ID',
      'Clear photo or video evidence of the defect',
      'Photos of the original packaging',
      'A description of the issue',
    ],
    footnote:
      'Possible resolutions (depending on availability):\n\n• Replacement of the defective item\n• A comparable substitute item\n• Store credit (Tokens)\n• A refund of the item\'s value\n\nPonPon may require the defective item to be returned before issuing compensation.',
  },
  {
    title: '6. Incorrect Items Shipped',
    body:
      'If you receive an item from a completely different series than what your Locker showed:\n\n• Email us within 7 days with photo evidence.\n• We will provide a prepaid return label.\n• Upon receipt of the incorrect item, we will ship the correct item (or provide store credit if unavailable).\n\nMinor cosmetic differences, common with capsule toys, do not qualify as incorrect shipment.',
  },
  {
    title: '7. Lost, Missing, or Stolen Packages',
    body:
      'If your tracking number shows "Delivered" but the package cannot be found:\n\n• Check with neighbors, front desk, or household members\n• Verify the shipping address\n• Contact the carrier to open a delivery trace\n\nPonPon will assist with the investigation.\n\nIf the carrier confirms loss, we may provide replacements or store credit based on inventory availability.\n\nPackages confirmed as successfully delivered by the carrier are not eligible for refunds.',
  },
  {
    title: '8. System, Payment, or Technical Errors',
    body:
      'PonPon will fully resolve any issues caused by system or payment errors, including:',
    bullets: [
      'Duplicate charges',
      'Spins that did not generate a result',
      'Locker not updating after a successful spin',
      'Shipping payment processed but shipment not created',
    ],
    footnote:
      'Depending on the situation, PonPon may provide:\n\n• Restored Tokens\n• Replacement items\n• A refund for the affected transaction',
  },
  {
    title: '9. Chargebacks',
    body:
      'If a chargeback is filed with your bank, your PonPon account may be temporarily suspended while the dispute is reviewed.\n\nTo prevent delays, please contact us first, we resolve most issues within 3-5 business days.',
  },
  {
    title: '10. Contact Information',
    body: 'Questions about the Return & Refund Policy should be sent to us at: salesponpon@gmail.com.',
  },
];

const ReturnRefundPage = () => {
  useEffect(() => {
    const smoothScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const ensureTop = () => {
      const doc = document?.documentElement;
      const body = document?.body;
      const current = (doc?.scrollTop || 0) + (body?.scrollTop || 0);
      if (current > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    };

    smoothScroll();
    const retryId = window.setTimeout(ensureTop, 220);
    const fallbackId = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 900);

    return () => {
      window.clearTimeout(retryId);
      window.clearTimeout(fallbackId);
    };
  }, []);

  return (
    <div className="flex flex-col gap-8">
      <Seo
        title="Return & Refund Policy | PonPon!"
        description="Review the PonPon! refund, Locker shipping, and incident policies so you know exactly how we protect every collector’s order."
        canonical="https://ponpon.app/return-refund"
        type="article"
      />
      <header>
        <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
          Return & Refund
        </h1>
        <p className="mt-2 text-xs uppercase tracking-wide text-ponpon-navy/60">
          Last Update: November, 2025
        </p>
        <div className="mt-4 space-y-3 text-sm leading-relaxed text-ponpon-navy/75">
          <p>Thank you for using PonPon!</p>
          <p>
            PonPon offers a unique gachapon experience where users purchase digital Tokens, use
            them to spin virtual capsule machines, and choose to ship the physical capsule toys they
            collect.
          </p>
          <p>
            Because of the collectible, randomized nature of capsule toys and the digital
            consumption of Tokens, this Return & Refund Policy applies to all purchases and
            shipments made through PonPon.
          </p>
        </div>
      </header>

      <div className="space-y-6">
        {RETURN_REFUND_SECTIONS.map(({ title, body, bullets, footnote }) => (
          <div key={title} className="space-y-2">
            <h2 className="text-base font-semibold text-ponpon-navy sm:text-lg">{title}</h2>
            {body ? (
              <p className="whitespace-pre-line text-sm leading-relaxed text-ponpon-navy/75">
                {body}
              </p>
            ) : null}
            {Array.isArray(bullets) ? (
              <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-ponpon-navy/75">
                {bullets.map((item, index) => (
                  <li key={`${title}-${index}`} className="whitespace-pre-line">
                    {item}
                  </li>
                ))}
              </ul>
            ) : null}
            {footnote ? (
              <p className="whitespace-pre-line text-sm leading-relaxed text-ponpon-navy/75">
                {footnote}
              </p>
            ) : null}
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
        >
          <span aria-hidden="true">←</span>
          Back to homepage
        </Link>
      </div>
    </div>
  );
};

export default ReturnRefundPage;
